import { useEffect, useRef, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { kycApi } from '../../api/endpoints'

/**
 * Verification documents — the SEAT-DOCS surface (Tej 2026-09-20).
 *
 * Every person provides an IDENTITY document and, for company members, a
 * PROOF OF EMPLOYMENT. Submission is required but never a waiting room:
 * uploading does not block anything — the account keeps working (a company
 * member keeps inheriting the company's verification) and the admin reviews
 * what lands in the console, intervening only when something looks off.
 *
 * Reuses the existing 3-step signed-URL flow (kycApi.upload → kyc/{uid}/ +
 * manifest carry-forward), so the console's People panel + Companies member
 * chips see these files exactly like every other KYC document.
 */

const ICON_ID = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="12" r="2.2" /><path d="M14 10h4" /><path d="M14 14h3" /><path d="M6.5 16.2a3 3 0 0 1 5 0" />
  </svg>
)
const ICON_EMPLOYMENT = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 12h18" />
  </svg>
)

const TILES: Array<{ docType: string; matches: string[]; title: string; hint: string; icon: ReactNode }> = [
  {
    docType: 'id',
    // A passport satisfies the identity requirement too.
    matches: ['id', 'passport'],
    title: 'Identity document',
    hint: 'Government-issued photo ID or passport.',
    icon: ICON_ID,
  },
  {
    docType: 'employment_proof',
    matches: ['employment_proof'],
    title: 'Proof of employment',
    hint: 'For company members — a letter, contract, or company ID card.',
    icon: ICON_EMPLOYMENT,
  },
]

export function VerificationDocs() {
  const [onFile, setOnFile] = useState<Map<string, string> | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const inputs = useRef<Record<string, HTMLInputElement | null>>({})
  const reduce = useReducedMotion()

  useEffect(() => {
    let cancelled = false
    kycApi.status()
      .then((s) => {
        if (cancelled) return
        setStatus(s.status ?? null)
        const m = new Map<string, string>()
        for (const d of (s.documents as Array<{ docType?: string; fileName?: string }>) ?? []) {
          if (d.docType) m.set(String(d.docType), String(d.fileName ?? 'uploaded'))
        }
        setOnFile(m)
      })
      .catch(() => { if (!cancelled) setOnFile(new Map()) })
    return () => { cancelled = true }
  }, [])

  async function pick(docType: string, file: File | null) {
    if (!file || busy) return
    setBusy(docType)
    setNote(null)
    try {
      await kycApi.upload(docType, file)
      setOnFile((prev) => new Map(prev ?? []).set(docType, file.name))
      setNote('Uploaded. Your account keeps working — our team reviews documents in the background.')
    } catch {
      setNote('Upload failed — check the file (PDF or image, under 10 MB) and try again.')
    } finally { setBusy(null) }
  }

  return (
    <section className="mt-6" data-testid="profile-verification-docs">
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-soft">Verification documents</h2>
      <p className="mt-1 text-[12.5px] text-soft">
        Every account provides an identity document; company seats also provide proof of employment.
        Uploading never locks your account — you keep browsing while we review.
      </p>

      {/* Action-required banner — nothing on file yet. No /kyc route on
          desktop: the CTA opens the identity file picker directly. */}
      {status === 'not_submitted' && (
        <div
          className="mt-3 rounded-md border p-3.5"
          style={{ background: 'rgba(201,169,74,0.10)', borderColor: 'rgba(201,169,74,0.35)' }}
          data-testid="verification-not-started-banner"
        >
          <p className="flex items-center gap-2 text-[13.5px] font-semibold text-ink">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gold" aria-hidden>
              <path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
            Verify your account to unlock pricing and supplier contacts
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-soft">
            Upload your verification documents below — our team reviews within 1–2 business days.
          </p>
          <button
            type="button"
            onClick={() => inputs.current['id']?.click()}
            className="mt-2.5 rounded-md bg-navy px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-navy-900"
            data-testid="verification-not-started-cta"
          >
            Upload now
          </button>
        </div>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {TILES.map((t, i) => {
          const have = onFile ? t.matches.find((m) => onFile.has(m)) : undefined
          const done = !!have
          return (
            <motion.div
              key={t.docType}
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: reduce ? 0 : 0.05 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-card border p-3.5 shadow-elev-0 transition-shadow"
              style={
                done
                  ? { borderColor: 'rgba(46,125,91,0.35)', background: 'rgba(46,125,91,0.06)' }
                  : { borderColor: 'var(--line)', background: 'var(--paper)' }
              }
            >
              <div className="flex items-start gap-3">
                {/* icon tile — greens on file, navy while needed */}
                <span
                  className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-[9px]"
                  style={
                    done
                      ? { background: 'rgba(46,125,91,0.14)', color: 'var(--success)', border: '1px solid rgba(46,125,91,0.35)' }
                      : { background: 'rgba(35,69,111,0.07)', color: 'var(--navy-500)', border: '1px solid var(--line)' }
                  }
                  aria-hidden
                >
                  {t.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-ink">{t.title}</span>
                    {onFile !== null && (
                      done
                        ? (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success" style={{ background: 'rgba(46,125,91,0.14)' }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 6 9 17l-5-5" /></svg>
                            on file
                          </span>
                        )
                        : <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-600" style={{ background: 'rgba(201,169,74,0.16)' }}>needed</span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[12px] text-soft">{t.hint}</p>
                  {done && onFile && (
                    <p className="mt-1 truncate text-[11.5px] text-muted" title={onFile.get(have!)}>{onFile.get(have!)}</p>
                  )}
                </div>
              </div>
              <input
                ref={(el) => { inputs.current[t.docType] = el }}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => pick(t.docType, e.target.files?.[0] ?? null)}
                data-testid={`verification-doc-input-${t.docType}`}
              />
              <button
                type="button"
                disabled={busy === t.docType || onFile === null}
                onClick={() => inputs.current[t.docType]?.click()}
                className={`mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-all active:scale-[0.98] disabled:opacity-50 ${
                  done
                    ? 'border border-line bg-paper text-navy-500 hover:border-navy-300 hover:text-ink'
                    : 'bg-navy text-white hover:bg-navy-900 shadow-elev-1'
                }`}
                data-testid={`verification-doc-upload-${t.docType}`}
              >
                {busy === t.docType
                  ? 'Uploading…'
                  : done
                    ? 'Replace'
                    : (<>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 3v12" /><path d="m7 8 5-5 5 5" /><path d="M5 21h14" /></svg>
                        Upload
                      </>)}
              </button>
            </motion.div>
          )
        })}
      </div>
      {note && <p className="mt-2 text-[12px] text-muted">{note}</p>}
    </section>
  )
}
